import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Flag } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface ClinicInfo {
  id: string;
  name: string;
  created_at: string;
  timezone: string;
  is_flagged: boolean;
  flag_note: string | null;
}

export default function SuperAdminClinicDetail() {
  const { clinicId } = useParams<{ clinicId: string }>();
  const navigate = useNavigate();

  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [policies, setPolicies] = useState<any[]>([]);
  const [acks, setAcks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Flag modal
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagNote, setFlagNote] = useState("");
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    if (clinicId) fetchAll();
  }, [clinicId]);

  async function fetchAll() {
    setLoading(true);

    const [clinicRes, membersRes, policiesRes] = await Promise.all([
      supabase
        .from("clinics")
        .select("id, name, created_at, timezone, is_flagged, flag_note")
        .eq("id", clinicId!)
        .single(),
      supabase.from("clinic_members").select("*").eq("clinic_id", clinicId!),
      supabase.from("policies").select("*").eq("clinic_id", clinicId!).order("updated_at", { ascending: false }),
    ]);

    if (clinicRes.data) setClinic(clinicRes.data as any);

    const membersData = membersRes.data || [];
    setMembers(membersData);

    const owner = membersData.find((m: any) => m.role === "owner");
    setOwnerEmail(owner?.email || null);

    const policiesData = policiesRes.data || [];
    setPolicies(policiesData);

    // Fetch versions, assignments & acks for this clinic's policies
    if (policiesData.length > 0) {
      const policyIds = policiesData.map((p: any) => p.id);

      const [versionsRes, assignmentsRes] = await Promise.all([
        supabase
          .from("policy_versions")
          .select("id, policy_id, version_number, version_status")
          .in("policy_id", policyIds),
        supabase
          .from("policy_assignments")
          .select("policy_id, user_id")
          .in("policy_id", policyIds),
      ]);

      const versionsData = versionsRes.data || [];
      const assignmentsData = assignmentsRes.data || [];

      // Fetch assignee profiles
      const assigneeIds = [...new Set(assignmentsData.map((a: any) => a.user_id))];
      let assigneeNameMap: Record<string, string> = {};
      if (assigneeIds.length > 0) {
        const { data: assigneeProfiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", assigneeIds);
        (assigneeProfiles || []).forEach((p: any) => {
          assigneeNameMap[p.id] = p.full_name;
        });
      }

      // Build policy→assignee name map (use first assignment per policy)
      const policyAssigneeMap: Record<string, string> = {};
      assignmentsData.forEach((a: any) => {
        if (!policyAssigneeMap[a.policy_id]) {
          policyAssigneeMap[a.policy_id] = assigneeNameMap[a.user_id] || "Unknown";
        }
      });

      // Attach latest version to each policy
      const versionMap: Record<string, any> = {};
      (versionsData || []).forEach((v: any) => {
        if (!versionMap[v.policy_id] || v.version_number > versionMap[v.policy_id].version_number) {
          versionMap[v.policy_id] = v;
        }
      });
      setPolicies(policiesData.map((p: any) => ({
        ...p,
        _latestVersion: versionMap[p.id],
        _assignedTo: policyAssigneeMap[p.id] || null,
      })));

      // Fetch acks
      const versionIds = (versionsData || []).map((v: any) => v.id);
      if (versionIds.length > 0) {
        const { data: acksData } = await supabase
          .from("policy_acknowledgements")
          .select("id, policy_version_id, acknowledged_by, acknowledged_at")
          .in("policy_version_id", versionIds)
          .order("acknowledged_at", { ascending: false })
          .limit(50);

        // Enrich with names
        const profileIds = [...new Set((acksData || []).map((a: any) => a.acknowledged_by))];
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", profileIds);

        const nameMap: Record<string, string> = {};
        (profilesData || []).forEach((p: any) => {
          nameMap[p.id] = p.full_name;
        });

        // Map version to policy name
        const versionToPolicyName: Record<string, string> = {};
        (versionsData || []).forEach((v: any) => {
          const pol = policiesData.find((p: any) => p.id === v.policy_id);
          if (pol) versionToPolicyName[v.id] = pol.name;
        });

        setAcks(
          (acksData || []).map((a: any) => ({
            ...a,
            staffName: nameMap[a.acknowledged_by] || "Unknown",
            policyName: versionToPolicyName[a.policy_version_id] || "Unknown",
          }))
        );
      }
    }

    setLoading(false);
  }

  async function toggleFlag() {
    if (!clinic) return;
    if (clinic.is_flagged) {
      // Unflag
      const { error } = await supabase
        .from("clinics")
        .update({ is_flagged: false, flag_note: null } as any)
        .eq("id", clinic.id);
      if (!error) {
        setClinic({ ...clinic, is_flagged: false, flag_note: null });
        toast.success("Clinic unflagged");
      }
    } else {
      setShowFlagModal(true);
      setFlagNote("");
    }
  }

  async function confirmFlag() {
    if (!clinic) return;
    setFlagging(true);

    const { error } = await supabase
      .from("clinics")
      .update({ is_flagged: true, flag_note: flagNote || null } as any)
      .eq("id", clinic.id);

    if (!error) {
      setClinic({ ...clinic, is_flagged: true, flag_note: flagNote || null });
      supabase.functions.invoke("send-flag-email", {
        body: { clinic_name: clinic.name, flag_note: flagNote || null },
      });
      toast.success("Clinic flagged");
    }

    setFlagging(false);
    setShowFlagModal(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        Clinic not found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-[hsl(var(--header-from))] to-[hsl(var(--header-to))] px-6 py-5 flex items-center gap-4">
        <button onClick={() => navigate("/superadmin")} className="text-white hover:text-white/80">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-semibold text-white flex-1">{clinic.name}</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleFlag}
          className="text-white hover:bg-white/10"
        >
          <Flag className={`h-5 w-5 ${clinic.is_flagged ? "fill-red-400 text-red-400" : ""}`} />
        </Button>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {clinic.is_flagged && (
          <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            🚩 Flagged{clinic.flag_note ? `: ${clinic.flag_note}` : ""}
          </div>
        )}

        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="policies">Policies ({policies.length})</TabsTrigger>
            <TabsTrigger value="users">Users ({members.length})</TabsTrigger>
            <TabsTrigger value="acks">Acknowledgments ({acks.length})</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="rounded-lg border bg-card p-6 space-y-3">
              <Row label="Clinic ID" value={clinic.id} mono />
              <Row label="Name" value={clinic.name} />
              <Row label="Joined" value={format(new Date(clinic.created_at), "MMMM d, yyyy")} />
              <Row label="Timezone" value={clinic.timezone} />
              <Row label="Owner Email" value={ownerEmail || "—"} />
            </div>
          </TabsContent>

          {/* Policies */}
          <TabsContent value="policies">
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {policies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                        No policies
                      </TableCell>
                    </TableRow>
                  ) : (
                    policies.map((p: any) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{p.status}</Badge>
                        </TableCell>
                        <TableCell>{p._latestVersion ? `v${p._latestVersion.version_number}` : "—"}</TableCell>
                        <TableCell>{p._assignedTo || "—"}</TableCell>
                        <TableCell>{format(new Date(p.updated_at), "MMM d, yyyy")}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m: any) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>{m.email}</TableCell>
                      <TableCell className="capitalize">{m.role}</TableCell>
                      <TableCell className="capitalize">{m.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Acknowledgments */}
          <TabsContent value="acks">
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {acks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                        No acknowledgments
                      </TableCell>
                    </TableRow>
                  ) : (
                    acks.map((a: any) => (
                      <TableRow key={a.id}>
                        <TableCell className="font-medium">{a.policyName}</TableCell>
                        <TableCell>{a.staffName}</TableCell>
                        <TableCell>{format(new Date(a.acknowledged_at), "MMM d, yyyy h:mm a")}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Flag Modal */}
      <Dialog open={showFlagModal} onOpenChange={(o) => !o && setShowFlagModal(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flag {clinic.name}</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Add a note (optional)"
            value={flagNote}
            onChange={(e) => setFlagNote(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFlagModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmFlag} disabled={flagging}>
              {flagging ? "Flagging…" : "Confirm Flag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-sm font-medium text-muted-foreground w-32">{label}</span>
      <span className={`text-sm text-foreground ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
